import styled, { css } from 'styled-components';
import { vars } from '../../theme';
import { transition } from '../../styles/animations';
import { applyOpacity } from '../../utils/color.utils';
import HBClickable from '../HBClickable/HBClickable';
import HBText from '../HBText/HBText';
import { customScrollBar } from '../../styles/scrollbar';
import { IPinProps } from './HBNewTable.types';
import { Checkbox } from '../HBCheckbox/HBCheckbox';

export const TABLE_CHECKBOX_COLUMN_WIDTH = 38;

export const NewTableStyledTableDiv = styled.div`
    display: table;
    width: 100%;
    table-layout: fixed;
`;
export const NewTableStyledTableTable = styled.table`
    display: table;
    width: 100%;
    table-layout: fixed;
`;

export const NewTableStyledThead = styled.thead`
    position: relative;
    height: 22px;
`;

export const NewTableStyledTbody = styled.tbody``;

export const NewTableStyledTh = styled.th<{ hide: boolean }>`
    ${({ hide }) =>
        hide &&
        css`
            opacity: 0;
            pointer-events: none;
        `};
`;

export const NewTableStyledTrHeader = styled.tr<{
    hasSpacer: boolean;
    $isSelectable: boolean;
}>`
    ${NewTableStyledTh} {
        padding-left: 8px;
        padding-right: 12px;
        ${({ hasSpacer }) =>
            !hasSpacer &&
            css`
                padding-bottom: 16px;
            `}
    }

    ${NewTableStyledTh}:first-child {
        padding-left: ${({ $isSelectable }) =>
            $isSelectable ? '12px' : '20px'};
        padding-right: 12px;
        ${Checkbox} {
            vertical-align: middle;
        }
    }

    ${NewTableStyledTh}:last-child {
        padding-right: 20px;
    }

    position: relative;
`;

export const NewTableStyledTd = styled.td`
    height: 62px;
`;

export const HBNewTableRowHoverVisisbleCellWrapper = styled.div`
    display: contents;
    opacity: 0;
    ${transition('opacity')};
`;

export const NewTableStyledTrRow = styled.tr<{
    expandOnRowClick: boolean;
    hasSpacer: boolean;
    isRowExpanded: boolean;
    $isSelectable: boolean;
}>`
    background: ${vars.colors.surface.container};
    ${({ expandOnRowClick }) => (expandOnRowClick ? `cursor: pointer;` : '')}
    ${NewTableStyledTd} {
        padding-left: 8px;
        padding-right: 12px;
    }

    ${NewTableStyledTd}:first-child {
        border-radius: ${vars.radius.xs} 0px 0px ${vars.radius.xs};
        padding-left: ${({ $isSelectable }) =>
            $isSelectable ? '12px' : '20px'};
        padding-right: 12px;
    }
    ${NewTableStyledTd}:last-child {
        border-radius: 0px ${vars.radius.xs} ${vars.radius.xs} 0px;
        padding-right: 20px;
        padding-left: 0;
    }
    position: relative;
    ${transition('box-shadow')}
    ${({ hasSpacer, isRowExpanded }) =>
        !hasSpacer &&
        isRowExpanded &&
        css`
            background: ${vars.colors.surface.itemInset};
        `}
    &:hover, &:focus {
        ${HBNewTableRowHoverVisisbleCellWrapper} {
            opacity: 1;
        }
        ${({ hasSpacer }) =>
            hasSpacer &&
            css`
                box-shadow: ${vars.shadow['10']};
            `}
    }
    &:hover {
        ${({ hasSpacer }) =>
            !hasSpacer &&
            css`
                background: ${vars.colors.surface.itemInsetHover};
            `}
    }
`;

export const NewTableLoaderContainer = styled.div`
    width: 100%;
    display: flex;
    justify-content: center;
    padding: 32px;
`;

export const HBNewTableCellText = styled(HBText).attrs({
    type: 'body14'
})<{
    maxWidth?: number;
}>`
    display: inline-block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    ${({ maxWidth }) =>
        maxWidth != null &&
        css`
            max-width: ${maxWidth}px;
        `};
`;

export const HBNewTableClickableCellWrapper = styled(HBClickable)`
    height: 100%;
    display: flex;
    align-items: center;
    ${({ isDisabled }) =>
        !isDisabled &&
        css`
            cursor: pointer;
        `};
`;

export const NewTableColGroup = styled.colgroup``;

export const NewTableCol = styled.col<{ $width?: string }>`
    ${({ $width }) =>
        $width &&
        css`
            min-width: ${$width};
            max-width: ${$width};
            width: ${$width};
        `};
`;

const pinnedStyles = (
    isLast: boolean,
    isShadowActive: boolean,
    left: number = 0
) => css`
    background-color: ${vars.colors.surface.container};
    z-index: 1;
    position: sticky;
    ${isLast
        ? css`
              right: 0;
          `
        : css`
              left: ${left}px;
          `}
    &::after {
        content: '';
        position: absolute;
        top: 0px;
        bottom: 0px;
        width: 12px;
        ${isLast
            ? css`
                  left: -12px;
              `
            : css`
                  right: -12px;
              `}
        background: linear-gradient(
                ${isLast ? '' : '-'}90deg,
                ${applyOpacity(vars.colors.surface.containerInverted, 0)} 0%,
                ${applyOpacity(vars.colors.surface.containerInverted, 2)} 50%,
                ${applyOpacity(vars.colors.surface.containerInverted, 8)} 100%
            );
        transition: opacity 50ms;
        opacity: 0;
        ${isShadowActive &&
        css`
            opacity: 1;
        `}
    }
`;

const pinnedHeaderStyles = css<IPinProps>`
    background-color: ${({ pinnedColumnHeaderBgColor }) =>
        pinnedColumnHeaderBgColor || vars.colors.surface.itemInset};
    position: sticky;
    z-index: 2;
    top: -22px;
`;

export const NewTableWrapper = styled.div<
    IPinProps & {
        $isSelectable: boolean;
        $isScrolled: boolean;
        $isScrolledToEnd: boolean;
        $maxHeight: string;
    }
>`
    overflow-y: auto;
    max-height: ${({ $maxHeight }) => $maxHeight};
    ${customScrollBar}

    [role='table'] {
        ${({ isFirstColumnPinned, $isSelectable, $isScrolled }) =>
            isFirstColumnPinned &&
            css`
                width: auto;
                min-width: 100%;

                thead tr > th:first-child {
                    ${pinnedHeaderStyles}
                }
                ${$isSelectable &&
                css`
                    thead tr > th:nth-child(2) {
                        ${pinnedHeaderStyles}
                        left: ${TABLE_CHECKBOX_COLUMN_WIDTH + 2}px;
                        // 2px prevents giggle effect when start scrolling (border)
                    }

                    thead tr > th:first-child {
                        width: 1%; // Safest approach to prevent the checkbox from taking up more space.
                        white-space: nowrap;
                    }
                `}

                thead tr > :first-child {
                    z-index: 3;
                    left: 0;
                    top: 0;
                }

                tbody tr.actual-row > :first-child {
                    ${pinnedStyles(false, $isScrolled)}
                }

                ${$isSelectable &&
                css`
                    tbody tr.actual-row > :nth-child(2) {
                        ${pinnedStyles(
                            false,
                            $isScrolled,
                            TABLE_CHECKBOX_COLUMN_WIDTH + 2 // 2px prevents giggle effect when start scrolling (border)
                        )}
                    }
                `}
            `}

        ${({ isLastColumnPinned, $isScrolledToEnd }) =>
            isLastColumnPinned &&
            css`
                width: auto;
                min-width: 100%;

                thead tr > th:last-child {
                    ${pinnedHeaderStyles}
                }

                thead tr > :last-child {
                    z-index: 3;
                    right: 0;
                    top: 0;
                }

                tbody tr.actual-row > :last-child {
                    ${pinnedStyles(true, !$isScrolledToEnd)}
                }
            `}
    }
`;
